import i18next, { type i18n as I18n, type Module } from 'i18next';
import ICU from 'i18next-icu';

import { type Locale, resources } from './index';

/** Arabic is right-to-left; French left-to-right (T4 §3.4). */
export function dirOf(locale: Locale): 'ltr' | 'rtl' {
  return locale === 'ar' ? 'rtl' : 'ltr';
}

/**
 * Locale used by Intl and ICU. Tunisian Arabic with Latin digits (BR-T-03: Western digits by default),
 * so `#` in plurals, numbers and dates never switch to Arabic-Indic digits.
 */
export function intlLocale(locale: Locale): string {
  return locale === 'ar' ? 'ar-TN-u-nu-latn' : 'fr-TN';
}

/**
 * BR-T-04: customers follow the phone language — the first of the browser's preferred languages that we
 * support (Arabic or French), French otherwise; a saved choice wins.
 */
export function detectLocale(languages: readonly string[], saved?: string | null): Locale {
  if (saved === 'fr' || saved === 'ar') return saved;
  for (const language of languages) {
    const code = language.toLowerCase();
    if (code.startsWith('ar')) return 'ar';
    if (code.startsWith('fr')) return 'fr';
  }
  return 'fr';
}

export const BUSINESS_TIME_ZONE = 'Africa/Tunis';

export function formatNumber(value: number, locale: Locale, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(intlLocale(locale), options).format(value);
}

/**
 * Dates are always shown in Tunis time (T4 §3.4), even on a phone set to another timezone, with a 24-hour
 * clock (CLDR's fr-TN would otherwise print "12:30 AM").
 */
export function formatDateTime(
  instant: number | Date,
  locale: Locale,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
): string {
  return new Intl.DateTimeFormat(intlLocale(locale), {
    hourCycle: 'h23',
    ...options,
    timeZone: BUSINESS_TIME_ZONE,
  }).format(instant);
}

/**
 * One i18next instance with ICU messages (plurals, select) and both catalogs loaded, so switching the
 * language never fetches anything. Apps add their React binding through `plugins`.
 */
export async function createI18n(locale: Locale, plugins: Module[] = []): Promise<I18n> {
  const instance = i18next.createInstance();
  instance.use(new ICU({ parseLngForICU: (lng: string) => intlLocale(lng === 'ar' ? 'ar' : 'fr') }));
  for (const plugin of plugins) instance.use(plugin);
  await instance.init({
    lng: locale,
    fallbackLng: 'fr',
    supportedLngs: ['fr', 'ar'],
    resources: { fr: { translation: resources.fr }, ar: { translation: resources.ar } },
    interpolation: { escapeValue: false },
    returnNull: false,
  });
  return instance;
}

/** Keeps `<html lang dir>` in step with the language (T4 §3.4, §6.1). */
export function applyDocumentLocale(doc: Document, locale: Locale): void {
  doc.documentElement.lang = locale;
  doc.documentElement.dir = dirOf(locale);
}
